import { Checkbox } from '@mui/material'
import { debounce } from 'lodash'
import { useEffect, useRef, useState } from 'react'

import { MrtCellProps } from '@/components'
import { cn } from '@/utils/cn'

export const CheckboxCell = <TData extends object>({
	cell,
	row,
	column,
	updateCellValue,
	isDisabled,
}: MrtCellProps<TData>) => {
	// Get the cell value and convert to boolean to ensure it's a valid checkbox state
	const cellValue = Boolean(cell?.getValue())

	// Local state for immediate UI feedback
	const [checked, setChecked] = useState(cellValue)

	// Keep local state in sync with cell value (important for DnD operations)
	useEffect(() => {
		setChecked(cellValue)
	}, [cellValue])

	// Create a debounced update function that persists across renders
	const debouncedUpdateRef = useRef(
		debounce((rowIndex, columnId, value) => {
			updateCellValue?.(rowIndex, columnId, value)
		}, 300),
	)

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const value = e.target.checked
		setChecked(value) // Update local state immediately for UI responsiveness
		debouncedUpdateRef.current(row?.index, column?.id, value)
	}

	const handleKeyDown = (e: React.KeyboardEvent<HTMLButtonElement>) => {
		if (e.key === 'Enter' && !isDisabled) {
			e.preventDefault()
			const newValue = !checked
			setChecked(newValue) // Update local state immediately for UI responsiveness
			debouncedUpdateRef.current(row?.index, column?.id, newValue)
		}
	}

	const className = cn('flex w-full', {
		'cursor-not-allowed': isDisabled,
	})

	return (
		<div className={className}>
			<Checkbox
				style={{ height: '28px' }}
				checked={checked}
				disabled={isDisabled}
				name={column?.header}
				onChange={handleChange}
				onKeyDown={handleKeyDown}
			/>
		</div>
	)
}
